import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  Checkbox,
  FormControlLabel,
  Link
} from '@mui/material';
import { Save as SaveIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';
import SignaturePad from '../SignaturePad';
import ManagerSelect from '../ManagerSelect';
import { getFormText, NO_AUTOFILL } from '../PolicyText';

const CompanyPolicyForm = ({ language = 'en', onSubmit, loading, disabled }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    employeeName: '',
    managerName: '',
    agreeTermsAndConditions: false
  });
  const [employeeSignature, setEmployeeSignature] = useState(null);
  const [managerSignature, setManagerSignature] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
      setFormData(prev => ({
        ...prev,
        employeeName: fullName
      }));
    }
  }, [user]);

  const t = {
    en: {
      relatedDoc: 'Related Document',
      docText: 'Please click on the file to open the full company policy.',
      policyTitle: 'Paramount Landscaping Company Policy 2020',
      agreeCheck: 'I have read and understood the company policy.',
      employeeName: 'Employee Name (Please Print)',
      employeeSignature: 'Employee Signature',
      managerName: 'Manager Name',
      managerSignature: 'Manager Signature',
      save: 'Save Form',
      requiredField: 'This field is required',
      mustAgree: 'You must agree to the terms and conditions',
      signatureRequired: 'Signature is required'
    },
    es: {
      relatedDoc: 'Documento Relacionado',
      docText: 'Haga clic en el archivo para abrir la política completa de la compañía.',
      policyTitle: 'Política de la Compañía Paramount Landscaping 2020',
      agreeCheck: 'He leído y entendido la política de la compañía.',
      employeeName: 'Nombre del Empleado (Por Favor Imprima)',
      employeeSignature: 'Firma del Empleado',
      managerName: 'Nombre del Gerente',
      managerSignature: 'Firma del Gerente',
      save: 'Guardar Formulario',
      requiredField: 'Este campo es obligatorio',
      mustAgree: 'Debe aceptar los términos y condiciones',
      signatureRequired: 'Se requiere firma'
    }
  };

  const labels = t[language] || t.en;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.employeeName.trim()) newErrors.employeeName = labels.requiredField;
    if (!formData.managerName.trim()) newErrors.managerName = labels.requiredField;
    if (!formData.agreeTermsAndConditions) newErrors.agreeTermsAndConditions = labels.mustAgree;
    if (!employeeSignature) newErrors.employeeSignature = labels.signatureRequired;
    if (!managerSignature) newErrors.managerSignature = labels.signatureRequired;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const combinedData = {
        ...formData,
        employeeSignature,
        managerSignature
      };
      onSubmit(combinedData, employeeSignature);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} autoComplete="off" data-lpignore="true" data-form-type="other">
      <Typography variant="h6" gutterBottom>
        {labels.relatedDoc}
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" paragraph>
          {labels.docText}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <PdfIcon color="error" />
          {/* The legacy PL Hub booklet PDFs (English and Spanish). */}
          <Link href={getFormText(language, 'text.companyPolicy.bookletFile')} target="_blank" rel="noopener noreferrer" underline="hover">
            {labels.policyTitle}
          </Link>
        </Box>
      </Alert>

      <FormControlLabel
        control={
          <Checkbox
            checked={formData.agreeTermsAndConditions}
            onChange={handleChange}
            name="agreeTermsAndConditions"
            disabled={disabled}
          />
        }
        label={labels.agreeCheck}
      />
      {errors.agreeTermsAndConditions && (
        <Typography color="error" variant="caption" display="block">
          {errors.agreeTermsAndConditions}
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Employee Section */}
      <Typography variant="h6" gutterBottom>
        {labels.employeeName}
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={labels.employeeName}
            name="employeeName"
            value={formData.employeeName}
            onChange={handleChange}
            required
            disabled={disabled}
            error={!!errors.employeeName}
            helperText={errors.employeeName}
            inputProps={NO_AUTOFILL}
          />
        </Grid>
      </Grid>

      <SignaturePad
        onSignature={setEmployeeSignature}
        label={labels.employeeSignature}
        language={language}
      />
      {errors.employeeSignature && (
        <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
          {errors.employeeSignature}
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Manager Section */}
      <Typography variant="h6" gutterBottom>
        {labels.managerName}
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ManagerSelect
            label={labels.managerName}
            value={formData.managerName}
            onChange={(managerName) => {
              setFormData((prev) => ({ ...prev, managerName }));
              if (errors.managerName) setErrors((prev) => ({ ...prev, managerName: '' }));
            }}
            required
            disabled={disabled}
            error={!!errors.managerName}
            helperText={errors.managerName}
            language={language}
          />
        </Grid>
      </Grid>

      <SignaturePad
        onSignature={setManagerSignature}
        label={labels.managerSignature}
        language={language}
      />
      {errors.managerSignature && (
        <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
          {errors.managerSignature}
        </Typography>
      )}

      {/* Submit Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          disabled={disabled || loading}
        >
          {labels.save}
        </Button>
      </Box>
    </Box>
  );
};

export default CompanyPolicyForm;

